import type { Deployment, Fleet, FortClass, FortClassId, Ruleset, ShipClass, ShipClassId } from './types.ts';

/** R-FLEET-01. Single source of truth for ship characteristics. */
export const SHIP_CLASSES: Readonly<Record<ShipClassId, ShipClass>> = {
  first_rate: {
    name: 'First rate, 100 guns',
    propulsion: 'sail',
    freshSpeed: 5,
    calmSpeed: 0,
    stormDrift: 3,
    shotsPerTurn: 3,
    range: 6,
    sinksAt: 7,
  },
  ship_of_line: {
    name: 'Ship of the line, 60 guns',
    propulsion: 'sail',
    freshSpeed: 5,
    calmSpeed: 0,
    stormDrift: 3,
    shotsPerTurn: 2,
    range: 5,
    sinksAt: 5,
  },
  sail_frigate: {
    name: 'Sailing frigate, 40 guns',
    propulsion: 'sail',
    freshSpeed: 6,
    calmSpeed: 0,
    stormDrift: 2,
    shotsPerTurn: 1,
    range: 5,
    sinksAt: 4,
  },
  brig: {
    name: 'Brig, 14 guns',
    propulsion: 'oars',
    freshSpeed: 5,
    calmSpeed: 1,
    stormDrift: 2,
    shotsPerTurn: 1,
    range: 3,
    sinksAt: 3,
  },
  cutter: {
    name: 'Cutter, 8 guns',
    propulsion: 'oars',
    freshSpeed: 6,
    calmSpeed: 1,
    stormDrift: 2,
    shotsPerTurn: 1,
    range: 1,
    sinksAt: 2,
  },
  galleon: {
    name: 'Galleon, 30 guns',
    propulsion: 'sail',
    freshSpeed: 4,
    calmSpeed: 0,
    stormDrift: 3,
    shotsPerTurn: 2,
    range: 3,
    sinksAt: 3,
  },
  galley: {
    name: 'Galley, 8 guns',
    propulsion: 'oars',
    freshSpeed: 5,
    calmSpeed: 3,
    stormDrift: 1,
    shotsPerTurn: 1,
    range: 3,
    sinksAt: 3,
  },
  ironclad_battery: {
    name: 'Steam ironclad battery, 40 heavy guns',
    propulsion: 'steam',
    freshSpeed: 4,
    calmSpeed: 2,
    stormDrift: 2,
    shotsPerTurn: 3,
    range: 6,
    sinksAt: 5,
  },
  steam_frigate: {
    name: 'Steam frigate, 30 guns',
    propulsion: 'steam',
    freshSpeed: 6,
    calmSpeed: 4,
    stormDrift: 1,
    shotsPerTurn: 1,
    range: 3,
    sinksAt: 4,
  },
  steam_corvette: {
    name: 'Steam corvette, 20 guns',
    propulsion: 'steam',
    freshSpeed: 6,
    calmSpeed: 4,
    stormDrift: 1,
    shotsPerTurn: 1,
    range: 3,
    sinksAt: 3,
  },
  paddle_steamer: {
    name: 'Paddle steamer, 10 guns',
    propulsion: 'steam',
    freshSpeed: 5,
    calmSpeed: 2,
    stormDrift: 2,
    shotsPerTurn: 1,
    range: 3,
    sinksAt: 3,
  },
  monitor: {
    name: 'Monitor, 2 heavy turret guns',
    propulsion: 'steam',
    freshSpeed: 3,
    calmSpeed: 2,
    stormDrift: 3,
    shotsPerTurn: 1,
    range: 3,
    sinksAt: 3,
  },
};

/** R-FLEET-01: the original 28-ship fleet of the board game. */
export const CLASSIC_FLEET: Fleet = {
  first_rate: 2,
  ship_of_line: 4,
  sail_frigate: 3,
  brig: 5,
  cutter: 2,
  galleon: 3,
  galley: 4,
  ironclad_battery: 1,
  steam_frigate: 1,
  steam_corvette: 1,
  paddle_steamer: 1,
  monitor: 1,
};

/** R-SETUP-02: 4 ships in each first-line port, 5 in each second-line port, 6 in the large one. */
export const CLASSIC_DEPLOYMENT: Deployment = { firstLine: 4, secondLine: 5, largePort: 6 };

/** R-FLEET-02 */
export const FORT_CLASSES: Readonly<Record<FortClassId, FortClass>> = {
  first_line: { name: 'First-line fort', shotsPerTurn: 3, range: 5, destroyedAt: 7 },
  second_line: { name: 'Second-line fort', shotsPerTurn: 2, range: 4, destroyedAt: 5 },
};

/** §16 Configurable options with their defaults. */
export const DEFAULT_RULESET: Ruleset = {
  hitChance: 1 / 4,
  oarsAndSteamUseBest: true,
  passThroughShips: true,
  landBlocksFire: true,
  largePortAssignment: 'random',
  firstSide: 'lot',
  diagonalSqueeze: false,
  volleyMinShooters: 2,
  portShipLimit: 6,
  fortRepairCrew: 3,
  fortRepairPerTurn: 2,
  royalPortHoldTurns: 3,
  fleet: CLASSIC_FLEET,
  deployment: CLASSIC_DEPLOYMENT,
  shipClasses: SHIP_CLASSES,
  fortClasses: FORT_CLASSES,
};

export function makeRuleset(overrides: Partial<Ruleset> = {}): Ruleset {
  const ruleset: Ruleset = { ...DEFAULT_RULESET, ...overrides };
  if (!(ruleset.hitChance > 0 && ruleset.hitChance <= 1)) {
    throw new RangeError('hitChance must be in (0, 1]');
  }
  for (const key of [
    'volleyMinShooters',
    'portShipLimit',
    'fortRepairCrew',
    'fortRepairPerTurn',
    'royalPortHoldTurns',
  ] as const) {
    if (!Number.isInteger(ruleset[key]) || ruleset[key] < 1) {
      throw new RangeError(`${key} must be a positive integer`);
    }
  }
  for (const [cls, n] of Object.entries(ruleset.fleet)) {
    if (!(cls in ruleset.shipClasses) || !Number.isInteger(n) || n < 0) {
      throw new RangeError(`fleet: ${cls} must be a known ship class with a count ≥ 0`);
    }
  }
  for (const [key, n] of Object.entries(ruleset.deployment)) {
    if (!Number.isInteger(n) || n < 1 || n > ruleset.portShipLimit) {
      throw new RangeError(`deployment.${key} must be an integer in 1..portShipLimit (R-MAP-06)`);
    }
  }
  return ruleset;
}

/** Ships in one side's fleet. */
export function fleetSize(fleet: Fleet): number {
  return Object.values(fleet).reduce((n, k) => n + k, 0);
}

export function isPowered(cls: ShipClass): boolean {
  return cls.propulsion !== 'sail';
}
